import React, { useEffect, useState } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const C = {
  bg:     '#0d1117',
  card:   '#161b22',
  border: '#21262d',
  text:   '#e6edf3',
  muted:  '#8b949e',
  blue:   '#4a9eff',
  green:  '#36d399',
  orange: '#f6c90e',
  roed:   '#f87171',
  gra:    '#2d3748',
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ color: C.muted, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        p.value !== null && (
          <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
            {p.name}: <strong>
              {p.dataKey === 'ctr_skaleret'
                ? `${(p.value / 10).toFixed(2)}%`
                : p.value?.toLocaleString('da-DK')}
            </strong>
          </div>
        )
      ))}
    </div>
  );
}

export default function SEOMomentumSR() {
  const [data, setData]         = useState([]);
  const [info, setInfo]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [fejl, setFejl]         = useState(null);
  const [visImpr, setVisImpr]   = useState(true);
  const [visKlik, setVisKlik]   = useState(true);
  const [visCtr, setVisCtr]     = useState(true);

  useEffect(() => {
    fetch('/api/os/strategisk/seo-momentum', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if ((d.fejl || d.ok === false) && d.data?.length === 0) {
          setFejl(d.besked || 'SEO-data utilgaengelig');
          return;
        }
        setInfo({ besked: d.besked, fejl: d.fejl ?? !d.ok });
        // Skalér CTR x10 for at vise pa samme akse som klik
        const mapped = (d.data || []).map(r => ({
          ...r,
          ctr_skaleret: r.ctr !== undefined ? parseFloat((r.ctr * 10).toFixed(2)) : null,
        }));
        setData(mapped);
      })
      .catch(e => setFejl(e.message))
      .finally(() => setLoading(false));
  }, []);

  const btnStyle = (active, farve) => ({
    padding:    '4px 12px',
    borderRadius: 6,
    border:     `1px solid ${C.border}`,
    background: active ? farve : C.card,
    color:      active ? '#fff' : C.muted,
    cursor:     'pointer',
    fontSize:   12,
    minHeight:  36,
  });

  if (loading) return <div style={{ padding: 32, color: C.muted, textAlign: 'center' }}>Henter SEO-data...</div>;

  if (fejl || data.length === 0) return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '20px 16px', color: C.text, fontFamily: 'system-ui, sans-serif' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>SEO-Momentum</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>Google Search Console · ugentlig aggregering</div>
      <div style={{
        background: C.card,
        border:     `1px solid ${C.border}`,
        borderRadius: 12,
        padding:    '32px 24px',
        textAlign:  'center',
        color:      C.muted,
      }}>
        <div style={{ fontSize: 24, marginBottom: 12 }}>◎</div>
        <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>SEO-data utilgaengelig</div>
        <div style={{ fontSize: 13 }}>{fejl || info.besked || 'Ingen data fra Search Console'}</div>
        <div style={{ fontSize: 12, marginTop: 12, color: C.muted }}>
          Tjek at GOOGLE_ANALYTICS_SERVICE_ACCOUNT er konfigureret i Key Vault
        </div>
      </div>
    </div>
  );

  // Aggregerede totaler
  const totalKlik  = data.reduce((s, d) => s + (d.klik || 0), 0);
  const totalImpr  = data.reduce((s, d) => s + (d.impressions || 0), 0);
  const snit_ctr   = data.length > 0
    ? (data.reduce((s, d) => s + (d.ctr || 0), 0) / data.length).toFixed(2)
    : 0;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '20px 16px', color: C.text, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>SEO-Momentum</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>Google Search Console · ugentlig aggregering</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={btnStyle(visImpr, C.gra)}   onClick={() => setVisImpr(v => !v)}>Impressions</button>
          <button style={btnStyle(visKlik, C.green)}  onClick={() => setVisKlik(v => !v)}>Klik</button>
          <button style={btnStyle(visCtr, C.orange)}  onClick={() => setVisCtr(v => !v)}>CTR</button>
        </div>
      </div>

      {/* KPI-kort */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total klik',        vaerdi: totalKlik.toLocaleString('da-DK'),  farve: C.green  },
          { label: 'Total impressions', vaerdi: totalImpr.toLocaleString('da-DK'),  farve: C.blue   },
          { label: 'Gns. CTR',          vaerdi: `${snit_ctr}%`,                     farve: C.orange },
          { label: 'Perioder (uger)',    vaerdi: data.length,                         farve: C.muted  },
        ].map(k => (
          <div key={k.label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 140,
          }}>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>{k.label}</div>
            <div style={{ color: k.farve, fontSize: 20, fontWeight: 700 }}>{k.vaerdi}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="uge" stroke={C.muted} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis yAxisId="left"  stroke={C.muted} tick={{ fontSize: 10 }} width={55}
              tickFormatter={v => v >= 1000 ? Math.round(v/1000) + 'k' : v}
            />
            <YAxis yAxisId="right" orientation="right" stroke={C.orange} tick={{ fontSize: 10 }} width={40}
              tickFormatter={v => `${(v/10).toFixed(1)}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />

            {visImpr && (
              <Area yAxisId="left" dataKey="impressions" name="Impressions"
                fill="#2d374888" stroke={C.blue} strokeWidth={1} fillOpacity={0.4}
              />
            )}
            {visKlik && (
              <Line yAxisId="left" dataKey="klik" name="Klik"
                stroke={C.green} strokeWidth={2} dot={false}
              />
            )}
            {visCtr && (
              <Line yAxisId="right" dataKey="ctr_skaleret" name="CTR (hoejre akse)"
                stroke={C.orange} strokeWidth={2} dot={false} strokeDasharray="4 2"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div style={{ color: C.muted, fontSize: 11, marginTop: 8, textAlign: 'right' }}>
        CTR-kurven er skaleret x10 for synlighed pa vaenstre akse
      </div>
    </div>
  );
}